package fr.frux.core.common.entitlements

import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.time.Instant
import kotlin.time.TimeMark

/**
 * Droits Frux+ du compte courant (#454), tels que `GET /auth/me` les a rendus au dernier appel.
 *
 * Ne garde que ce que le socle publicitaire (#527, ADR 0030 point 6) consomme : savoir si le compte
 * est abonné, et jusqu'à quand la publicité est coupée. Les dates restent brutes plutôt qu'un booléen
 * figé à la réception : un `AD_FREE` de 24 h expire pendant que l'app tourne, l'appelant compare via
 * [isAdFreeAt] à l'heure du serveur que prolonge [serverClock], jamais à l'horloge murale de
 * l'appareil (#527).
 */
data class AccountEntitlements(
    val premium: Boolean,
    /** Fin du `PREMIUM`, `null` s'il n'expire pas (ou si [premium] est faux). */
    val premiumExpiresAt: Instant?,
    /** Fin la plus lointaine des `AD_FREE` actifs, [Instant.DISTANT_FUTURE] pour un droit sans fin. */
    val adFreeUntil: Instant?,
    /**
     * Heure du serveur à la réception de ces droits (en-tête `Date` de `/auth/me`, #527). `null`
     * quand elle n'est pas connue : en-tête absent, ou masqué par CORS sur le web.
     */
    val serverClock: ServerClock? = null,
    /**
     * Compte auquel ces droits appartiennent (#531), `null` pour [NONE]. La vidéo récompensée le
     * relaie à Google comme `user_id` du rappel SSV : sans compte, pas de vidéo proposée.
     */
    val accountId: Long? = null,
    /** Source du droit `PREMIUM` retenu (#456), `null` quand elle n'est pas connue ou sans premium. */
    val premiumSource: PremiumSource? = null,
    /**
     * Référence d'achat opaque du compte (#457, ADR 0033), jamais l'email ni [accountId]. Passée au
     * store comme `obfuscatedAccountId` : le serveur refuse un jeton d'achat rattaché à un autre
     * compte. `null` pour [NONE] ou une API antérieure.
     */
    val purchaseAccountRef: String? = null,
) {
    fun isPremiumAt(now: Instant): Boolean = premium && (premiumExpiresAt == null || now < premiumExpiresAt)

    /** Sans publicité : abonné, ou `AD_FREE` non expiré (vidéo récompensée, #531). */
    fun isAdFreeAt(now: Instant): Boolean = isPremiumAt(now) || (adFreeUntil != null && now < adFreeUntil)

    companion object {
        /** Anonyme, déconnecté, ou compte sans droit. */
        val NONE = AccountEntitlements(premium = false, premiumExpiresAt = null, adFreeUntil = null)
    }
}

/**
 * Origine d'un droit `PREMIUM` (#456). Le serveur parle un vocabulaire ouvert : toute valeur que ce
 * client ne connaît pas tombe sur [OTHER] plutôt que de faire échouer la lecture.
 */
enum class PremiumSource {
    /** Offert à la main depuis le back-office (ADR 0016, amendement du 2026-09-27). */
    MANUAL,
    GOOGLE_PLAY,
    STRIPE,
    APP_STORE,
    OTHER,
    ;

    companion object {
        fun fromWire(value: String?): PremiumSource? =
            value?.let { raw -> entries.firstOrNull { it.name == raw } ?: OTHER }
    }
}

/**
 * Heure du serveur ancrée sur un repère monotone (#527). Comparer les échéances du serveur à
 * l'horloge de l'appareil montrerait la publicité à un abonné dont le téléphone avance, et
 * prolongerait un `AD_FREE` sur un téléphone qui retarde. On relève donc l'heure du serveur à la
 * réception, puis on la fait avancer en temps monotone, que ni l'heure murale ni un changement de
 * fuseau ne déplacent.
 *
 * Égalité par identité : deux ancres prises à des instants différents ne sont jamais « la même
 * heure », et [AccountEntitlements] reste comparable sur ses droits quand l'ancre est absente.
 *
 * Sommeil profond (#527) : sur Android, `TimeSource.Monotonic` repose sur `System.nanoTime`, qui
 * s'arrête quand l'appareil dort. [now] retarde alors sur l'heure réelle du serveur, jamais ne
 * l'avance. L'écart va dans le sens sûr : un droit sans pub paraît durer plus longtemps, la
 * publicité ne revient jamais trop tôt pour un abonné. Il reste borné : le socle publicitaire relit
 * `/auth/me` à chaque nouvelle session (retour après 30 minutes en arrière-plan), ce qui réancre
 * l'horloge. Une source `elapsedRealtime` en `expect`/`actual` ne corrigerait pas tout pour autant,
 * puisque la minuterie d'échéance (`delay` sur le thread principal) compte elle aussi en temps
 * d'éveil. On garde donc la source commune.
 */
class ServerClock(
    private val serverTimeAtMark: Instant,
    private val mark: TimeMark,
) {
    /** Heure estimée du serveur maintenant. */
    fun now(): Instant = serverTimeAtMark + mark.elapsedNow()
}

/**
 * Source observable de [AccountEntitlements] (#454). Vit dans `core-common`, comme
 * `SessionExpiryNotifier`, pour qu'un module core (`core-ads`, #527) l'observe sans dépendre de
 * `:shared`, où le dépôt d'authentification l'écrit.
 *
 * `null` = non résolu : rien n'a encore été lu pour la session courante. Distinct de
 * [AccountEntitlements.NONE] (anonyme ou sans droit) pour qu'au démarrage à froid d'un compte Frux+
 * le socle publicitaire ne prenne pas l'attente de `/auth/me` pour une absence de droit. Nullable
 * plutôt qu'un type scellé : un seul état sans charge utile, et `?.` dit déjà « pas encore su ».
 *
 * Transitions : [update] après un `/auth/me` réussi, [clear] à la déconnexion ou quand la session est
 * résolue anonyme, [unresolve] à l'ouverture d'une nouvelle session (le compte a pu changer). Un
 * échec réseau de `/auth/me` ne touche à rien, la valeur précédente reste.
 *
 * Génération : [clear] et [unresolve] changent de session, ils l'incrémentent. Une réponse de
 * `/auth/me` partie avant ce changement (A en vol, déconnexion, connexion de B) est périmée,
 * [updateIf] l'ignore au lieu d'écraser l'état de la session courante.
 */
@SingleIn(AppScope::class)
@Inject
class EntitlementsHolder {
    private val _state = MutableStateFlow<AccountEntitlements?>(null)
    val state: StateFlow<AccountEntitlements?> = _state.asStateFlow()

    // Lu et comparé sur le même thread que les écritures dans l'usage nominal (un suspend appelé
    // depuis Main), un compare-and-set atomique serait superflu ici.
    var generation: Long = 0
        private set

    /** Publie [entitlements] si aucune session n'a commencé ou fini depuis [expectedGeneration]. */
    fun updateIf(
        expectedGeneration: Long,
        entitlements: AccountEntitlements,
    ): Boolean {
        if (expectedGeneration != generation) return false
        _state.value = entitlements
        return true
    }

    /** Résolu sans droit : déconnexion, session anonyme. */
    fun clear() {
        generation++
        _state.value = AccountEntitlements.NONE
    }

    /** Retour à « non résolu » : nouvelle session dont les droits ne sont pas encore lus. */
    fun unresolve() {
        generation++
        _state.value = null
    }
}

/**
 * Relecture des droits Frux+ (`GET /auth/me`), demandée par le socle publicitaire (#527) à chaque
 * nouvelle session et à l'échéance d'un premium daté. Même renversement que `PushTokenSink` : un
 * module core déclare le besoin, `:shared` le fournit, sans dépendance de core vers `:shared`.
 *
 * Best-effort : ne lève pas, un échec laisse [EntitlementsHolder] tel quel.
 */
fun interface EntitlementsRefresher {
    suspend fun refresh()
}
